/**
 * Achievements (plan doc 13).
 *
 * Every achievement is a counter and a list of thresholds. The counters live
 * on the server and are the only durable record; this decides what tier a
 * count is worth, what is claimable, and what claiming pays.
 */

export type AchievementMetric =
  | 'longestStreak'
  | 'totalXp'
  | 'skillsMastered'
  | 'perfectLessons'
  | 'micPasses'
  | 'songsThreeStars'
  | 'earCorrect'
  | 'unitsCompleted'
  | 'ensembleWeeks'
  | 'legendaryLessons'
  | 'chordChanges'
  | 'longTones'
  | 'bestSpeedRound'
  | 'cosmeticsOwned'
  | 'lateLessons'
  | 'earlyLessons'
  | 'marathonDays'
  | 'following';

export type AchievementCategory = 'habit' | 'learning' | 'playing' | 'competition' | 'collection';

export interface AchievementDef {
  id: string;
  name: string;
  /** One line, in the app's voice, for the card. */
  description: string;
  metric: AchievementMetric;
  category: AchievementCategory;
  /** Ascending; reaching `tiers[i]` earns tier `i + 1`. */
  tiers: readonly number[];
  /** Cosmetic id awarded with the top tier, when there is one. */
  topReward?: string;
  /** Cosmetics awarded at other tiers, by one-based tier number. */
  tierRewards?: Readonly<Record<number, string>>;
  /** Hidden until it has been started, for the joke ones. */
  secret?: boolean;
}

/** Gems for reaching each tier, by one-based tier number (plan doc 13). */
export const ACHIEVEMENT_TIER_GEMS: readonly number[] = [25, 50, 100, 150, 200, 300, 500];

export function tierGems(tier: number): number {
  if (tier <= 0) return 0;
  return ACHIEVEMENT_TIER_GEMS[Math.min(tier, ACHIEVEMENT_TIER_GEMS.length) - 1] ?? 0;
}

/** Gems for going from one claimed tier to another, which may skip tiers. */
export function gemsBetween(fromTier: number, toTier: number): number {
  let total = 0;
  for (let tier = fromTier + 1; tier <= toTier; tier += 1) total += tierGems(tier);
  return total;
}

/** The highest tier a count has reached; 0 when it has reached none. */
export function tierFor(definition: AchievementDef, progress: number): number {
  let tier = 0;
  for (const threshold of definition.tiers) {
    if (progress >= threshold) tier += 1;
    else break;
  }
  return tier;
}

export interface AchievementState {
  achievementId: string;
  progress: number;
  /** Highest tier reached. */
  tier: number;
  /** Highest tier the learner has collected the gems for. */
  claimedTier: number;
}

export interface AchievementView extends AchievementState {
  definition: AchievementDef;
  /** The count the next tier needs, or null at the top. */
  nextThreshold: number | null;
  /** 0–1 towards the next tier, or 1 at the top. */
  fraction: number;
  /** Tiers reached but not yet claimed. */
  claimable: number;
  gemsWaiting: number;
  /** Cosmetic ids that claiming would hand over. */
  cosmeticsWaiting: string[];
}

/** Everything a card needs, computed from the count. */
export function achievementView(
  definition: AchievementDef,
  state: AchievementState,
): AchievementView {
  const tier = tierFor(definition, state.progress);
  const top = definition.tiers.length;
  const nextThreshold = tier >= top ? null : (definition.tiers[tier] ?? null);
  const previous = tier === 0 ? 0 : (definition.tiers[tier - 1] ?? 0);

  const fraction =
    nextThreshold === null
      ? 1
      : Math.max(
          0,
          Math.min(1, (state.progress - previous) / Math.max(1, nextThreshold - previous)),
        );

  const cosmetics: string[] = [];
  for (let t = state.claimedTier + 1; t <= tier; t += 1) {
    const reward = definition.tierRewards?.[t];
    if (reward !== undefined) cosmetics.push(reward);
    if (t === top && definition.topReward !== undefined) cosmetics.push(definition.topReward);
  }

  return {
    ...state,
    tier,
    definition,
    nextThreshold,
    fraction,
    claimable: Math.max(0, tier - state.claimedTier),
    gemsWaiting: gemsBetween(state.claimedTier, tier),
    cosmeticsWaiting: cosmetics,
  };
}

/** How much a finished session adds to each metric. */
export interface AchievementFacts {
  kind: string;
  perfect: boolean;
  legendary?: boolean;
  micPasses?: number;
  earCorrect?: number;
  stars?: number;
  chordChanges?: number;
  longTones?: number;
  speedRoundScore?: number;
  endedHour: number;
  /** Lessons the learner has finished today, including this one. */
  lessonsToday?: number;
}

/** Metrics that are a best-so-far rather than a running total. */
export const ACHIEVEMENT_HIGH_WATER: readonly AchievementMetric[] = [
  'longestStreak',
  'bestSpeedRound',
  'totalXp',
  'skillsMastered',
  'cosmeticsOwned',
  'unitsCompleted',
];

export function isAchievementHighWater(metric: AchievementMetric): boolean {
  return ACHIEVEMENT_HIGH_WATER.includes(metric);
}

/**
 * The increments a session makes.
 *
 * Only the counters a session can move are here; `totalXp`, `skillsMastered`,
 * `longestStreak`, `unitsCompleted` and `cosmeticsOwned` are read from the
 * profile rather than accumulated, because they already exist elsewhere and two
 * sources of truth for the same number will eventually disagree.
 */
export function achievementProgress(
  facts: AchievementFacts,
): Partial<Record<AchievementMetric, number>> {
  const out: Partial<Record<AchievementMetric, number>> = {};
  const add = (metric: AchievementMetric, amount: number): void => {
    if (amount > 0) out[metric] = (out[metric] ?? 0) + amount;
  };
  const isLesson = facts.kind === 'lesson' || facts.kind === 'checkpoint';

  if (isLesson && facts.perfect) add('perfectLessons', 1);
  if (isLesson && facts.legendary === true) add('legendaryLessons', 1);
  if (isLesson && facts.endedHour >= 23) add('lateLessons', 1);
  if (isLesson && facts.endedHour < 7) add('earlyLessons', 1);
  add('micPasses', facts.micPasses ?? 0);
  add('earCorrect', facts.earCorrect ?? 0);
  add('chordChanges', facts.chordChanges ?? 0);
  add('longTones', facts.longTones ?? 0);
  if (facts.kind === 'song' && (facts.stars ?? 0) >= 3) add('songsThreeStars', 1);
  if ((facts.speedRoundScore ?? 0) > 0) out.bestSpeedRound = facts.speedRoundScore ?? 0;
  if ((facts.lessonsToday ?? 0) === 10) add('marathonDays', 1);

  return out;
}
